import { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

import { Portal } from 'components-ref/atoms'
import { useDisableBodyScroll } from 'helpers/hooks/useDisableBodyScroll/useDisableBodyScroll'

import { type IProps } from './modal.types'
import { IconModalClose } from '../../../assets/svg/sprite'
import colors from '../../../styles/colors'

type ModalExtraProps = {
  height?: string
  minHeight?: string
  footer?: React.ReactNode
  fixedFooter?: boolean
  className?: string
  hideCloseIcon?: boolean
  padding?: string
}

export const Modal = ({
  children,
  footer,
  onClose,
  title,
  isLayerTransparent = true,
  hasShadow = false,
  closeOnLayerClick = true,
  paddingBottom = 2,
  maxWidth = 70,
  height,
  minHeight,
  fixedFooter = false,
  className,
  hideCloseIcon = false,
  padding
}: IProps & ModalExtraProps) => {
  useDisableBodyScroll()

const contentRef = useRef<HTMLDivElement>(null)
const [isShortModal, setIsShortModal] = useState(false)
const [keyboardHeight, setKeyboardHeight] = useState(0)
useEffect(() => {
    if (process.env.NODE_ENV === 'production') return

    const handleMockKeyboard = (event: Event) => {
        const customEvent = event as CustomEvent<number>

        setKeyboardHeight(
            Math.max(0, customEvent.detail)
        )
    }

    window.addEventListener(
        'mock-modal-keyboard',
        handleMockKeyboard
    )

    return () => {
        window.removeEventListener(
            'mock-modal-keyboard',
            handleMockKeyboard
        )
    }
}, [])
useEffect(() => {
    const content = contentRef.current
    if (!content) return

    const updateHeight = () => {
        const rootFontSize = parseFloat(
            getComputedStyle(document.documentElement).fontSize
        )

        const contentHeight = Math.max(
            content.scrollHeight,
            content.offsetHeight
        )

        setIsShortModal(contentHeight <= 45 * rootFontSize)
    }

    const observer = new ResizeObserver(updateHeight)

    observer.observe(content)
    updateHeight()

    return () => observer.disconnect()
}, [])

useEffect(() => {
    type VirtualKeyboard = {
        overlaysContent: boolean
        boundingRect: { height: number }
        addEventListener: (
            type: string,
            listener: () => void
        ) => void
        removeEventListener: (
            type: string,
            listener: () => void
        ) => void
    }

    const keyboard = (
        navigator as Navigator & {
            virtualKeyboard?: VirtualKeyboard
        }
    ).virtualKeyboard

    const initialViewportHeight =
        window.visualViewport?.height ?? window.innerHeight

    const update = () => {
        const viewportHeight =
            window.visualViewport?.height ?? window.innerHeight

        const viewportReduction = Math.max(
            0,
            initialViewportHeight - viewportHeight
        )

        const keyboardGeometryHeight =
            keyboard?.boundingRect.height ?? 0

        setKeyboardHeight(
            Math.max(
                keyboardGeometryHeight,
                viewportReduction
            )
        )
    }

    if (keyboard) {
        keyboard.addEventListener('geometrychange', update)
    }

    window.visualViewport?.addEventListener('resize', update)
    window.addEventListener('resize', update)

    update()

    return () => {
        keyboard?.removeEventListener('geometrychange', update)
        window.visualViewport?.removeEventListener('resize', update)
        window.removeEventListener('resize', update)
    }
}, [])

  useEffect(() => {
    const isOpen = document.body.classList.contains('portal--open')

    if (!isOpen) {
      document.body.classList.add('portal--open')
    }

    return () => {
      if (!isOpen) {
        document.body.classList.remove('portal--open')
      }
    }
  }, [])

  const handleBackgroundClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation()
    if (closeOnLayerClick) onClose()
  }

  return (
    <Portal>
      <StyledModal
        $isShortModal={isShortModal}
        $keyboardHeight={keyboardHeight}
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
        }}
      >
        <Background
          onClick={handleBackgroundClick}
          $isLayerTransparent={isLayerTransparent}
        />

        <Content
          ref={contentRef}
          className={className}
          $hasShadow={hasShadow}
          $paddingBottom={paddingBottom}
          $maxWidth={maxWidth}
          $height={height}
          $minHeight={minHeight}
          $fixedFooter={fixedFooter}
          $padding={padding}
        >
          {!hideCloseIcon && (
            <CloseModal onClick={onClose}>
              <IconModalClose color="#A3A3A3" />
            </CloseModal>
          )}

          {title && <Title>{title}</Title>}

          <Body
            $paddingBottom={paddingBottom}
            $fixedFooter={fixedFooter}
          >
            {children}
          </Body>

          {footer ? <Footer $fixedFooter={fixedFooter}>{footer}</Footer> : null}
        </Content>
      </StyledModal>
    </Portal>
  )
}

interface StyledModalProps {
    $isShortModal: boolean
    $keyboardHeight: number
}

const StyledModal = styled.div<StyledModalProps>`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    z-index: 99999999;
    padding: 2rem;

    display: flex;
    justify-content: center;
    align-items: center;

    height: 100dvh;
    box-sizing: border-box;

    @media (max-width: 768px) {
        height: ${({ $isShortModal, $keyboardHeight }) =>
            $isShortModal && $keyboardHeight > 0
                ? `max(0px, calc(100dvh - ${$keyboardHeight}px))`
                : '100dvh'};

        padding-top: 1rem;
        padding-bottom: 1rem;

        transition: height 180ms ease-out;
    }
`

interface BackgroundProps {
  $isLayerTransparent?: boolean
}

const Background = styled.div<BackgroundProps>`
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100dvh;

  background: ${props =>
    props.$isLayerTransparent ? 'transparent' : '#121418'};

  opacity: ${props => (props.$isLayerTransparent ? 1 : 0.4)};
`

const CloseModal = styled.div`
  position: absolute;
  top: 1rem;
  right: 2rem;
  cursor: pointer;

  svg {
    width: 1.4rem;
    height: 1.4rem;
  }
`

interface ContentProps {
  $hasShadow?: boolean
  $paddingBottom: number
  $maxWidth?: number
  $height?: string
  $minHeight?: string
  $fixedFooter?: boolean
  $padding?: string
}
const Content = styled.div<ContentProps>`
  max-height: 80vh;
  border-radius: 2rem;
  width: 100%;
  position: relative;
  padding: ${props => props.$padding ?? '2rem'};
  background-color: white;

  max-width: ${props => `${props.$maxWidth}rem`};

  height: ${props => props.$height ?? props.$minHeight ?? 'auto'};
  min-height: ${props => props.$minHeight ?? 'auto'};

  box-shadow: ${props =>
    props.$hasShadow ? '0 0.4rem 5.4rem rgba(0, 0, 0, 0.25)' : 'none'};

  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (max-width: 768px) {
    max-height: calc(100% - 2rem);
    flex-shrink: 1;
  }
`

const Title = styled.p`
  font-weight: 600;
  color: ${colors.blueDark};
  line-height: 21.94px;
  font-size: 1.8rem;
  padding-top: 1rem;
  padding-bottom:1rem;
  flex: 0 0 auto;
`

interface BodyProps {
  $paddingBottom?: number
  $fixedFooter?: boolean
}

const Body = styled.div<BodyProps>`
  min-height: 0;
  overflow-y: auto;

  flex: ${props => (props.$fixedFooter ? '1 1 auto' : '0 1 auto')};

  padding-bottom: ${props => `${props.$paddingBottom ?? 0}rem`};
`

interface FooterProps {
  $fixedFooter?: boolean
}

const Footer = styled.div<FooterProps>`
  flex: 0 0 auto;
  background: white;

  padding-top: 2rem;
  padding-bottom: 2rem;

  display: flex;
  justify-content: center;
  align-items: center;
`